import React, { useState, useEffect } from 'react'
import c from 'classnames'
import get from 'lodash/get'
import isNull from 'lodash/isNull'
import { connect } from 'react-redux'

import Icon from 'discada/dist/Icon'
import Tag from 'discada/dist/Tag'

import { addToPageState } from 'actions/pageStateActions'
import { getUserLocation, getProfileOk } from 'selectors/appStatusSelectors'
import {
  getTaggedCustomerFromState,
  getLocationLoading,
  getCustomerVerifications,
} from 'selectors/appStatusSelectors'
import { getCustomerTradeEquity } from 'selectors/naughtySelectors'
import { moneySuperShort } from 'utils'

import { getUserLocation as geolocate } from 'actions/appStatusActions'
import { getQueryValue } from 'selectors/pageStateSelectors'

import { INTENTS } from 'reducers/quoteReducers'

import './index.scss'

///////////////////////////////////////////////////////

function V({ v, a }) {
  return (
    <Icon
      fixedWidth
      className={c('verified-icon', {
        active: a,
        verified: v,
        'not-verified': !v,
      })}
      icon={a ? 'arrow-down' : v ? 'check' : 'plus'}
    />
  )
}

export const ProfileSection = connect(
  op => op,
  { addToPageState },
)(props => {
  let {
      className,
      children,
      verified,
      icon,
      reaction = INTENTS.getOTD,
      step,
      activeStep,
      addToPageState,
      mini,
      miniLabel,
      active = activeStep && step && step === activeStep,
    } = props,
    hasVerifiedIcon = props.verifiedIcon || props.hasOwnProperty('verified'),
    verifiedIcon = props.verifiedIcon || <V v={verified} a={active} />,
    params = { reaction, step }

  return (
    <div
      className={c('user-profile-section', className, { verified, active })}
      onClick={() => addToPageState(params)}
    >
      <span>
        <Icon className="user-profile-section-icon" icon={icon} />
        <span className="user-profile-section-label">
          {mini ? miniLabel : children}
        </span>
        {miniLabel && (
          <span className="user-profile-section-label-mini">{miniLabel}</span>
        )}
      </span>
      {hasVerifiedIcon && verifiedIcon}
    </div>
  )
})

let getAccuracy = args => {
    return Math.min(
      0.99,
      Math.max(0.5, args.reduce((ret, v) => (v ? ret + 1 : ret)) / args.length),
    )
  },
  getSignalStrength = args =>
    Math.min(
      Math.max(
        0,
        args.reduce((ret, v) => (v ? ret + 1 : ret), Math.round(Math.random())),
      ),
      5,
    )

///////////////////////////////////////////////////////

export function Profile(props) {
  let {
      location = {},
      rebateCats,
      trades,
      tradeEquity,
      firstName,
      creditScore,
      gettingLocation,
      customerVerified,
      tradesVerified,
      rebatesVerified,
      creditVerified,
      locationVerified,
      hasNoRebates,
      hasNoTrade,
      mini,
      customer,
    } = props,
    [_, setTime] = useState(), // eslint-disable-line
    [signalStrength, setSignalStrength] = useState(null),
    tradesLabel = tradesVerified
      ? trades.length > 1
        ? `${trades.length} trades`
        : [get(trades, '[0].year'), get(trades, '[0].model')].join(' ')
      : 'Value YOUR Trade!',
    symbol =
      tradeEquity > 0 ? (
        <Icon className="equity-symbol" icon="caret-up" />
      ) : (
        <Icon className="equity-symbol" icon="caret-down" />
      ),
    equityAbs = Math.abs(tradeEquity)

  useEffect(() => {
    const timer = setTimeout(() => {
      let ss = getSignalStrength([
        locationVerified,
        rebatesVerified,
        tradesVerified,
        creditVerified,
        customerVerified,
      ])

      setSignalStrength(ss)
      setTime(Date.now())
    }, 1333)

    return () => clearTimeout(timer)
  })

  let locationLabel = (
    location.city ||
    location.taxRegionLabel ||
    location.zipCode ||
    customer.zipCode ||
    ''
  ).split(',')[0]

  if (!props.profileOk) return null

  return (
    <div className={c('user-profile', { 'user-profile-mini': mini })}>
      <div className="user-profile-inner">
        {!mini && (
          <ProfileSection
            {...props}
            className="no-arrow user-profile-info"
            icon="hand-pointer"
            step="contact"
          />
        )}

        <ProfileSection
          {...props}
          miniLabel="Location"
          icon="map-marker-alt"
          className="user-profile-location"
          step="location"
          verified={locationVerified}
        >
          {gettingLocation && 'Getting Location Info...'}
          {!gettingLocation &&
            (locationVerified ? `For ${locationLabel}` : 'Set YOUR Location!')}
        </ProfileSection>
        <ProfileSection
          {...props}
          miniLabel="Trade"
          icon="car-alt"
          className="user-profile-trade"
          step="trades"
          verified={tradesVerified}
        >
          {!hasNoTrade && tradesLabel}
          {!hasNoTrade && equityAbs > 0 && tradesVerified && (
            <>
              {symbol}
              {moneySuperShort(equityAbs, true)}
            </>
          )}
          {hasNoTrade && <em>No Trade</em>}
        </ProfileSection>
        <ProfileSection
          {...props}
          miniLabel="Incentives"
          icon="search-dollar"
          className="user-profile-rebates"
          step="rebates"
          verified={rebatesVerified}
        >
          {rebatesVerified ? (
            hasNoRebates ? (
              <em>No Rebates Selected</em>
            ) : (
              `${rebateCats.join(', ')}${
                rebateCats.length === 1 ? ' Incentives' : ''
              }`
            )
          ) : (
            'Find Incentives!'
          )}
        </ProfileSection>
        <ProfileSection
          {...props}
          miniLabel="Credit"
          icon="credit-card"
          className="user-profile-credit"
          step="credit"
          verified={creditVerified}
        >
          {creditVerified
            ? `${parseInt(creditScore, 10) === 0 ? 800 : creditScore}+ Credit`
            : 'Add YOUR Credit!'}
        </ProfileSection>
        <ProfileSection
          {...props}
          icon="file-signature"
          className="user-profile-accuracy"
          step="contact"
          verifiedIcon={
            <Tag
              size="small"
              className={c('signal-strength', {
                loading: isNull(signalStrength),
                high: signalStrength > 3,
              })}
            >
              <Icon
                spin={isNull(signalStrength)}
                prefix={isNull(signalStrength) ? 'fas' : 'fad'}
                icon={
                  isNull(signalStrength)
                    ? 'spinner-third'
                    : signalStrength === 5
                    ? 'signal'
                    : signalStrength === 0
                    ? 'signal-slash'
                    : `signal-${signalStrength}`
                }
              />
            </Tag>
          }
        >
          {firstName ? `${firstName}'s` : 'YOUR'} Deal Power
        </ProfileSection>
      </div>
    </div>
  )
}

function mapStateToProps(state, op) {
  let customer = getTaggedCustomerFromState(state),
    { trades = [], selectedRebateCategories = [] } = customer,
    tradeEquity = getCustomerTradeEquity(state),
    rebateCats = selectedRebateCategories.map(c => c.Name),
    gettingLocation = getLocationLoading(state),
    creditScore = get(customer, 'creditScore'),
    location = getUserLocation(state),
    verifications = getCustomerVerifications(state),
    locationVerified = verifications.location,
    rebatesVerified = verifications.rebates,
    tradesVerified = verifications.trades,
    creditVerified = verifications.credit,
    customerVerified = verifications.customer

  let ret = {
    ...op,
    customer,
    hasNoRebates: customer.hasNoRebates,
    hasNoTrade: customer.hasNoTrade,
    sellMyTrade: customer.sellMyTrade,
    firstName: get(customer, 'firstName'),
    profileOk: op.force || getProfileOk(state),
    location,
    trades,
    creditScore,
    tradeEquity,
    rebateCats,
    gettingLocation,
    locationVerified,
    rebatesVerified,
    tradesVerified,
    creditVerified,
    customerVerified,
    activeStep: getQueryValue(state, 'step'),
    accuracy: getAccuracy([
      locationVerified,
      rebatesVerified,
      tradesVerified,
      creditVerified,
      customerVerified,
    ]),
  }

  return ret
}

export default connect(
  mapStateToProps,
  {
    addToPageState,
    geolocate,
  },
)(Profile)
